import { useScroll, useTransform, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Inner = styled.div`
  position: relative;
`;

const Row = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 55px;
  padding-top: ${({ $first }) => ($first ? 0 : "40px")};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const YearCol = styled.div`
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  align-self: flex-start;
  z-index: 40;

  @media (max-width: 768px) {
    position: static;
    gap: 0;
  }
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--color-bg);
  border: 2px solid var(--color-primary);
  z-index: 1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const YearLabel = styled.h3`
  margin: 0;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-meta);
  letter-spacing: 0.04em;
  line-height: 1;
  min-width: 40px;
`;

const ContentCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const TrackLine = styled.div`
  position: absolute;
  left: 7px;
  top: 0;
  width: 2px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(128, 128, 128, 0.15) 8%,
    rgba(128, 128, 128, 0.15) 92%,
    transparent 100%
  );
  pointer-events: none;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FillLine = styled(motion.div)`
  position: absolute;
  left: 7px;
  top: 0;
  width: 2px;
  border-radius: 2px;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    var(--color-primary) 8%,
    var(--color-primary) 92%
  );

  @media (max-width: 768px) {
    display: none;
  }
`;

export const Timeline = ({ data }) => {
  const ref = useRef(null);
  const containerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    setHeight(el.getBoundingClientRect().height);
    const ro = new ResizeObserver(() => {
      setHeight(el.getBoundingClientRect().height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 90%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <Container ref={containerRef}>
      <Inner ref={ref}>
        {data.map((item, index) => (
          <Row key={index} $first={index === 0}>
            <YearCol>
              <Dot />
              <YearLabel>{item.title}</YearLabel>
            </YearCol>
            <ContentCol>{item.content}</ContentCol>
          </Row>
        ))}
      </Inner>

      <TrackLine style={{ height: height }} />
      <FillLine style={{ height: heightTransform, opacity: opacityTransform }} />
    </Container>
  );
};
